import React, { PureComponent, RefObject } from 'react';
import { Flex } from '../../common/Flex';
import { FlowSection, FlowSectionComponent } from './flows/FlowSection';
import {
    ExtendedMonitoredTv,
    MonitorAdditionalTrafficVolumeRequest,
    MonitoredEvent,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    TvMeteoAlert,
    TvSetForecastProgress,
} from 'app/model/SOL49';
import Utils from '../../../common/util/Utils';
import GroupedFlowSectionRedux, { GroupedFlowSection } from './flows/GroupedFlowSection';
import MeasureUtils from '../../../common/util/MeasureUtils';
import KeyUtils from '../../../common/util/KeyUtils';
import { FlightId, HotspotDto, MonitoredLocationType, TimePeriod } from 'app/model/CORE';
import { IFlowSection } from './flows/IFlowSection';
import { NumberOfFlowColumns } from '../../../common/context/AppConstants';
import { ServiceFactory } from 'app/service/ServiceFactory';
import memoizeOne from 'memoize-one';
import { RootState } from '../../../redux/store';
import { connect, ConnectedProps } from 'react-redux';
import {
    selectUserCustomFlows,
    selectAllFlows,
    createFlowSection,
} from '../../../redux/features/dashboard/user-monitored-flows-slice';
import { SUPERVISED_AREAS } from '../common/DashboardConstants';
import AppContext from 'app/common/context/AppContext';

const mapStateToProps = (state: RootState) => {
    return {
        userMonitoredFlowsState: state.userMonitoredFlowsState,
        customFlowsSections: selectUserCustomFlows(state),
        allFlowsSections: selectAllFlows(state),
    };
};

const mapDispatchToProps = {
    createFlowSection,
};

const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });
type PropsFromRedux = ConnectedProps<typeof connector>;

type NetworkMonitoringFlowsProps = PropsFromRedux & {
    currentTime: Date;
    monitoredTvs: Array<ExtendedMonitoredTv>;
    monitoredMeasures: Array<MonitoredMeasure>;
    monitoredEvents?: Array<MonitoredEvent>;
    pendingMeasureProposals: Array<MonitoredMcdmMeasure>;
    tvSetMap: { [key: string]: string };
    tvSetProgressMap?: { [key: string]: TvSetForecastProgress };
    tvSetlastUpdate?: { [key: string]: Date };
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    nbColumns: NumberOfFlowColumns;
    textFilter: string;

    selectedTvs: Array<ExtendedMonitoredTv>;

    onSelectionChange?(tvIds: Array<ExtendedMonitoredTv>, selected: boolean);

    tvMeteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
    hotspots?: { [key: string]: Array<HotspotDto> };
};

interface NetworkMonitoringFlowsState {}

enum SectionNames {
    WHOLE_NETWORK = 'WHOLE_NETWORK',
}

export class NetworkMonitoringFlowsComponent extends PureComponent<
    NetworkMonitoringFlowsProps,
    NetworkMonitoringFlowsState
> {
    flowSectionRefMap: Map<string, RefObject<IFlowSection>> = new Map();

    componentDidMount() {
        let additionalTVs = [];
        this.props.customFlowsSections.forEach(customFlow => {
            Utils.addAllIfNeeded(additionalTVs, customFlow.tvIds);
        });
        // todo verify behaviour when mount / unmount
        additionalTVs?.length && this.monitorExtraTvIds(additionalTVs); // Add TVs to the list of additional TVs, in case the server was restarted
    }

    monitorExtraTvIds = (additionalTVs: Array<string>) => {
        const request = {
            additionalTVs: additionalTVs,
        } as MonitorAdditionalTrafficVolumeRequest;
        ServiceFactory.sol49Service()
            .monitorAdditionalTrafficVolume(request)
            .then(reply => {
                //TODO: forgotten?
                console.log(reply);
            });
    };

    getOrCreateSectionRef = (flowSectionName: string): RefObject<IFlowSection> => {
        if (this.flowSectionRefMap.has(flowSectionName)) {
            return this.flowSectionRefMap.get(flowSectionName);
        }
        const ref = React.createRef<IFlowSection>();
        this.flowSectionRefMap.set(flowSectionName, ref);
        return ref;
    };

    getFilteredMonitoredTvList = memoizeOne(
        (monitoredTvs: Array<ExtendedMonitoredTv>, tvSetMap: { [key: string]: string }, textFilter: string) => {
            // TODO: Move to utility class?
            let zones = AppContext.getZones();
            zones = zones?.flat(1) || [];
            let filteredTvs = monitoredTvs.filter(monitoredTv => {
                let vsName = tvSetMap[monitoredTv.refLocation.id];
                if (vsName) return Utils.contains(zones, vsName.substring(0, 2));
                console.warn('Missing TvSet for', monitoredTv.refLocation.id);
                return false;
            });
            if (textFilter) {
                let textFilterUC: Array<string> = Utils.toUpperCase(textFilter).split(' ');
                return filteredTvs?.filter(monitoredTv => {
                    let tvId = Utils.toUpperCase(monitoredTv.refLocation.id);
                    let tvSet = Utils.toUpperCase(tvSetMap[tvId]);
                    //if the query text has a * uses the simpleWildcardEquals, otherwise checks if a tv or tvSet contains the given query text
                    const matchingTextFilters = textFilterUC.filter(textFilterEl => {
                        const re = Utils.wildcardToRegex(textFilterEl);
                        return re.test(tvId) || re.test(tvSet);
                    });
                    //return tvId?.indexOf(textFilterUC) >= 0 || tvSet?.indexOf(textFilterUC) >= 0
                    return matchingTextFilters.length > 0;
                });
            }
            return filteredTvs;
        },
    );

    /*
     * Function that allows to retrieve all displayed TVs in "WHOLE NETWORK" section, including text filtering and particular filter settings.
     * This is used by parent component to get all visible TVs.
     */
    getDisplayedMonitoredTvs = (): Array<ExtendedMonitoredTv> => {
        const wholeNetworkSection = this.flowSectionRefMap.get(SectionNames.WHOLE_NETWORK)?.current;
        return wholeNetworkSection ? wholeNetworkSection.getDisplayedMonitoredTvs() : [];
    };

    renderCreateCustomSection = (key: string, onCreateSection: () => void) => {
        return (
            <Flex key={key} className="create-custom-flow-section" style={{ flex: 'none' }}>
                <Flex direction="vertical">
                    <div
                        className="visible-on-hover"
                        style={{ padding: '0.25em', fontWeight: 'bold' }}
                        onClick={onCreateSection}>
                        CREATE A CUSTOM SECTION
                    </div>
                </Flex>
            </Flex>
        );
    };

    render() {
        return (
            <Flex direction="vertical">
                {this.props.allFlowsSections.map((monitoredFlow, idx) => {
                    const monitoredTvList = this.props.monitoredTvs?.filter(monitoredTv =>
                        Utils.contains(monitoredFlow.tvIds, monitoredTv.refLocation.id),
                    );
                    const monitoredTvIds = monitoredTvList?.map(monitoredTv => monitoredTv.refLocation.id) || [];
                    const monitoredMeasures = this.props.monitoredMeasures?.filter(monitoredMeasure =>
                        Utils.contains(monitoredTvIds, MeasureUtils.getTvLocation(monitoredMeasure.measure).id),
                    );
                    const selectedTvs = this.props.selectedTvs?.filter(tv =>
                        Utils.contains(monitoredTvIds, KeyUtils.MONITORED_TV_KEY_PROVIDER.getKey(tv)),
                    );

                    // TVs that have been requested to be monitored, but are not yet part of the dataset
                    const otherTvIds = monitoredFlow.tvIds.filter(tvId => !Utils.contains(monitoredTvIds, tvId));
                    const otherTvs: ExtendedMonitoredTv[] = otherTvIds.map(otherTvId => {
                        return {
                            refLocation: {
                                id: otherTvId,
                                type: MonitoredLocationType.TV,
                            },
                        } as ExtendedMonitoredTv;
                    });
                    monitoredTvList?.push(...otherTvs);

                    const meteoAlerts =
                        this.props.tvMeteoAlerts &&
                        Object.fromEntries(
                            Object.entries(this.props.tvMeteoAlerts).filter(
                                ([tv]) => monitoredTvIds.includes(tv) || otherTvIds.includes(tv),
                            ),
                        );

                    return [
                        this.renderCreateCustomSection('create-custom-section-' + idx, () =>
                            this.props.createFlowSection({ idx: idx }),
                        ),
                        <FlowSection
                            ref={this.getOrCreateSectionRef(monitoredFlow.flowName) as RefObject<FlowSectionComponent>}
                            uuid={monitoredFlow.uuid}
                            key={'flow-' + monitoredFlow.uuid}
                            title={monitoredFlow.flowName}
                            currentTime={this.props.currentTime}
                            monitoredTvList={monitoredTvList}
                            tvSetMap={this.props.tvSetMap}
                            tvsOccupancyPeriods={this.props.tvsOccupancyPeriods}
                            monitoredMeasures={monitoredMeasures}
                            nbColumns={this.props.nbColumns}
                            selectedTvs={selectedTvs}
                            moveUpEnabled={idx > 0}
                            moveDownEnabled={idx < this.props.allFlowsSections.length - 1}
                            onSelectionChange={this.props.onSelectionChange}
                            meteoAlerts={meteoAlerts}
                            events={this.props.monitoredEvents}
                        />,
                    ];
                })}
                {this.renderCreateCustomSection('create-custom-section-last', () => this.props.createFlowSection({}))}
                <GroupedFlowSectionRedux
                    ref={this.getOrCreateSectionRef(SectionNames.WHOLE_NETWORK) as RefObject<GroupedFlowSection>}
                    key={SectionNames.WHOLE_NETWORK}
                    title="WHOLE NETWORK"
                    currentTime={this.props.currentTime}
                    monitoredTvList={this.getFilteredMonitoredTvList(
                        this.props.monitoredTvs,
                        this.props.tvSetMap,
                        this.props.textFilter,
                    )}
                    tvSetMap={this.props.tvSetMap}
                    tvSetProgressMap={this.props.tvSetProgressMap}
                    tvSetlastUpdate={this.props.tvSetlastUpdate}
                    monitoredMeasures={this.props.monitoredMeasures}
                    pendingMeasureProposals={this.props.pendingMeasureProposals}
                    tvsOccupancyPeriods={this.props.tvsOccupancyPeriods}
                    nbColumns={this.props.nbColumns}
                    selectedTvs={this.props.selectedTvs}
                    onSelectionChange={this.props.onSelectionChange}
                    supervisedAreas={SUPERVISED_AREAS}
                    tvMeteoAlerts={this.props.tvMeteoAlerts}
                    monitoredEvents={this.props.monitoredEvents}
                    hotspots={this.props.hotspots}
                />
                <Flex style={{ height: '-webkit-fill-available' }} />{' '}
                {/** Fill available space so that content above is not stretched */}
            </Flex>
        );
    }
}

export default connector(NetworkMonitoringFlowsComponent);
